import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';

const LINKS = [
  { to: '/', label: 'Home' },
  { to: '/career-guidance', label: 'Career Guidance' },
  { to: '/career-paths', label: 'Career Paths' },
  { to: '/career-roadmaps', label: 'Roadmaps' },
  { to: '/resources', label: 'Resources' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,box-shadow] duration-300 ${
          scrolled ? 'border-slate-200/70 bg-white/90 shadow-card' : 'border-transparent bg-white/70'
        }`}
        data-testid="main-navbar"
      >
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="CareerWithRaaj home">
            <img src="/logo-wordmark.png" alt="CareerWithRaaj" className="h-9 w-auto" data-testid="nav-logo-image" />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-coral ${
                    isActive ? 'text-coral' : 'text-ink/70'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/book-consultation"
              data-testid="nav-book-consultation-cta"
              className="hidden rounded-full bg-coral px-6 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep sm:inline-flex"
            >
              Book Consultation
            </Link>
            <button
              className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-white text-navy lg:hidden"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-button"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy"
            data-testid="mobile-nav-drawer"
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="rounded-xl bg-white px-3 py-2">
                <img src="/logo-wordmark.png" alt="CareerWithRaaj" className="h-7 w-auto" />
              </span>
              <button
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 text-white"
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                >
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `block py-3 font-display text-3xl font-bold tracking-tight transition-colors ${
                        isActive ? 'text-coral' : 'text-white hover:text-coral'
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <Link
                to="/book-consultation"
                data-testid="mobile-nav-book-cta"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-coral py-4 text-base font-semibold text-white transition-colors hover:bg-coral-deep"
              >
                Book Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
